import { useEffect, useState } from "react";

import { t } from "../i18n";
import { rememberedValue, rememberValue } from "../lib/hooks";
import type { Worker } from "../lib/workingOn";
import { Modal } from "./ui";

/**
 * "Samuel is working on this customer" (ADR 0268).
 *
 * Two things, from one list of who else is on the customer:
 *
 * - A pop-up the first time each person's stay is seen. It names them, says
 *   which job they have open and since when, and asks the reader to check
 *   with them before calling, texting or changing anything. One button, Got
 *   it. A second person arriving later pops it up again for them; the same
 *   stay is never announced twice, and if they leave before Got it the
 *   pop-up goes with them. Someone who leaves and comes back is a new stay.
 *   A stay lasts while any of their tabs is open: closing the first of two
 *   tabs is not leaving, so the one still open is not announced again.
 * - A pill beside the customer's name for as long as anyone else is still
 *   on them, so the warning does not vanish with the pop-up.
 *
 * Which stays have been announced is kept in the tab's sessionStorage, per
 * customer, not in component state: the screen can be redrawn from scratch
 * (a refresh, the shell remounting it) while the colleague is still there,
 * and a pop-up the rep already pressed Got it on must not come back.
 *
 * Nothing is blocked. The point is that nobody oversteps by accident, not
 * that one rep locks the other out: the rep who was asked to pick up where
 * a colleague left off has every reason to be here.
 */

const firstName = (w: Worker) => w.name.trim().split(/\s+/)[0] || w.name;

/**
 * One stay, as each of this person's open tabs names it: the person, from
 * the moment that tab opened the customer. The stay was announced if any
 * one of these was.
 */
const stayKeys = (w: Worker) =>
  (w.tabsSince ?? [w.since]).map((since) => `${w.profileId}@${since}`);

const announcedKey = (contactId: string) => `working-on-announced:${contactId}`;

function readAnnounced(contactId: string): Set<string> {
  const raw = rememberedValue(announcedKey(contactId));
  if (!raw) return new Set();
  try {
    const parsed: unknown = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
  } catch {
    return new Set();
  }
}

function fmtClock(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

/** "Samuel", "Samuel and Lysa", "Samuel, Lysa and Carlos". */
export function listNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} ${t("and")} ${names[names.length - 1]}`;
}

function whereTheyAre(w: Worker): string {
  const since = fmtClock(w.since);
  if (w.jobNumber != null) {
    return since
      ? t("has job #{n} open since {time}", { n: w.jobNumber, time: since })
      : t("has job #{n} open", { n: w.jobNumber });
  }
  return since
    ? t("has the customer file open since {time}", { time: since })
    : t("has the customer file open");
}

export function WorkingOnNotice({
  contactId,
  others,
  customerName,
}: {
  contactId: string;
  others: Worker[];
  customerName: string;
}) {
  /** The profiles the open pop-up is about; empty when it is closed. */
  const [popped, setPopped] = useState<string[]>([]);

  useEffect(() => {
    const announced = readAnnounced(contactId);
    const before = announced.size;
    const arrivals = others.filter(
      (w) => !stayKeys(w).some((key) => announced.has(key)),
    );
    // Every tab of theirs is recorded, the ones opened after the pop-up too,
    // so whichever tab they close last, the stay is still one already seen.
    for (const w of others) for (const key of stayKeys(w)) announced.add(key);
    if (announced.size !== before) {
      rememberValue(announcedKey(contactId), JSON.stringify([...announced]));
    }
    if (!arrivals.length) return;
    setPopped((p) => [...p, ...arrivals.map((w) => w.profileId)]);
  }, [contactId, others]);

  // A colleague who has left is not someone to check with any more.
  const showing = others.filter((w) => popped.includes(w.profileId));

  const names = listNames(others.map(firstName));

  return (
    <>
      {others.length > 0 && (
        <span
          className="workingon"
          title={others.map((w) => `${w.name} ${whereTheyAre(w)}`).join("\n")}
        >
          {others.length === 1
            ? t("{names} is on this customer", { names })
            : t("{names} are on this customer", { names })}
        </span>
      )}
      {showing.length > 0 && (
        <Modal
          title={
            showing.length === 1
              ? t("{names} is working on this customer", {
                  names: listNames(showing.map(firstName)),
                })
              : t("{names} are working on this customer", {
                  names: listNames(showing.map(firstName)),
                })
          }
          onClose={() => setPopped([])}
          actions={
            <button type="button" onClick={() => setPopped([])}>
              {t("Got it")}
            </button>
          }
        >
          <ul className="workingon-list">
            {showing.map((w) => (
              <li key={w.profileId}>
                <b>{w.name}</b> {whereTheyAre(w)}.
              </li>
            ))}
          </ul>
          <p>
            {t(
              "Check with {names} before you call, text or change anything for {customer}, so you don't step on each other.",
              {
                names: listNames(showing.map(firstName)),
                customer: customerName,
              },
            )}
          </p>
        </Modal>
      )}
    </>
  );
}
